import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Label, Input, Button } from "reactstrap";
import Flatpickr from "react-flatpickr";
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { ownPersonalInfo, updateOwnPersonalInfo } from '../../slices/auth/settings/thunk';
import ErrorModal from '../../Components/Common/ErrorModal';
import { getOwnPersonalInfoSuccess, resetErrors } from '../../slices/auth/settings/reducer';
import CustomSelect from "../../Components/Common/CustomSelect";
import country from '../../common/country';
import { useLocation } from 'react-router-dom';
import { setInnerPreloader } from '../../slices/layouts/reducer';
import { replace } from "redux-first-history";
import Preloader from '../../Components/Common/Preloader';
import { setBreadcrumb } from '../../slices/auth/menu/reducer';
import CustomSelectWithSearch from '../../Components/Common/CustomSelectWithSearch';
import { formatDate } from '../../slices/helper';
import moment from 'moment-timezone';


/**
 * PersonalDetails component representing the user's personal details settings page.
 * This component fetches and displays the user's personal details such as name,
 * phone number, email, joining date, job title, timezone, address, and allows
 * the user to update this information through a form.
 * @component
 */
function PersonalDetails() {
  const dispatch = useDispatch();
  const location = useLocation();

  const selectStates = createSelector(state => state, state => ({
    previousLocations: state.router.previousLocations,
    personalInfoData: state.Settings.personalInfo,
    personalInfoErr: state.Settings.personalInfoErr,
    personalInfoErrMsg: state.Settings.personalInfoErrMsg,
  }));
  const { previousLocations, personalInfoData, personalInfoErr, personalInfoErrMsg } = useSelector(selectStates);

  const [personalDetails, setPersonalDetails] = useState({});
  const [countrySelect, setCountrySelect] = useState("");
  const [timezones, setTimezones] = useState([]);
  const [timezoneSelect, setTimezoneSelect] = useState('');

  useEffect(() => {
    if (previousLocations[0].location.state === null && (previousLocations[0].action === "PUSH" || previousLocations[0].action === "POP")) {
      dispatch(setInnerPreloader(true));
      dispatch(ownPersonalInfo()).then(res => {
        dispatch(replace(location.pathname, res.payload));
        dispatch(setInnerPreloader(false));
      });
    } else if (previousLocations[0].location.state !== null) {
      dispatch(getOwnPersonalInfoSuccess(previousLocations[0].location.state));
    }
  }, [location]);

  useEffect(() => {
    if (personalInfoData !== null) {
      setPersonalDetails(personalInfoData);
      setCountrySelect(personalInfoData.country);
      setTimezoneSelect(personalInfoData.timeZone);
    }
  }, [personalInfoData]);

  useEffect(() => {
    dispatch(setBreadcrumb({ pageTitle: 'Settings', title: [{ name: 'Personal Details', src: window.location.href }] }));
  }, []);

  useEffect(() => {
    const timezoneNames = moment.tz.names();
    const formattedTimezones = timezoneNames.map((zone) => {
      const offset = moment.tz(zone).utcOffset();
      const hours = Math.floor(Math.abs(offset) / 60);
      const minutes = Math.abs(offset) % 60;
      const sign = offset >= 0 ? '+' : '-';

      return { name: `${zone} GMT ${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}` };
    });

    setTimezones(formattedTimezones);
  }, []);

  /**
   * Handles the update of personal details by preventing the default form submission,
   * extracting form data, and dispatching an action to update the user's personal information.
   *
   * @param {Event} e - The form submission event.
   * @returns {void}
   */
  function updatePersonalDetails(e) {
    e.preventDefault();
    let dataObj = {};
    const formData = new FormData(e.target);
    formData.forEach((value, name) => {
      dataObj[name] = value;
    });
    dataObj.country = countrySelect;
    dataObj.picture = (personalDetails?.picture ? personalDetails?.picture : null);
    dataObj.email = personalDetails.email;
    dataObj.joiningDate = personalDetails.joiningDate;
    dataObj.timeZone = timezoneSelect;
    dispatch(updateOwnPersonalInfo(dataObj));
  }
  return (
    <React.Fragment>
      {personalDetails.hasOwnProperty("firstName") ?
        <Form onSubmit={updatePersonalDetails}>
          <Row>
            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="firstName" className="form-label">First
                  Name</Label>
                <Input type="text" className="form-control" id="firstName" name="firstName"
                  placeholder="Enter your firstname" defaultValue={(personalDetails.firstName || "")} />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="lastName" className="form-label">Last
                  Name</Label>
                <Input type="text" className="form-control" id="lastName" name="lastName"
                  placeholder="Enter your lastname" defaultValue={(personalDetails.lastName || "")} />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="phoneNumner" className="form-label">Phone
                  Number</Label>
                <Input type="text" className="form-control"
                  id="phoneNumber" name="phoneNumber"
                  placeholder="Enter your phone number"
                  defaultValue={(personalDetails.phoneNumber || "")} />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="email" className="form-label">Email
                  Address</Label>
                <Input type="email" className="form-control" id="email" name="email"
                  placeholder="Enter your email"
                  defaultValue={(personalDetails.email || "")} disabled />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="joiningDate" className="form-label">Joining
                  Date</Label>
                <Input
                  type='text'
                  className='form-control'
                  id='joiningDate'
                  name='joiningDate'
                  value={(personalDetails?.joiningDate ? formatDate(Number(personalDetails?.joiningDate)) : '')}
                  disabled
                  onChange={() => { }} />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="country" className="form-label">Country</Label>
                <CustomSelectWithSearch
                  placeholder='Select Country'
                  options={country}
                  onChange={(item) => setCountrySelect(item)}
                  value={personalDetails.country}
                />
                {/* <Input type="text" className="form-control" id="country" name="country"
                              placeholder="Country" defaultValue={(personalDetails.country || "")} /> */}
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="jobTitle"
                  className="form-label">Job Title</Label>
                <Input type="text" className="form-control"
                  id="jobTitle" name="jobTitle" placeholder="Job Title"
                  defaultValue={(personalDetails.jobTitle || "")} />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="timeZone"
                  className="form-label">Timezone</Label>
                <CustomSelectWithSearch
                  placeholder='Select Timezone'
                  onChange={(item) => setTimezoneSelect(item.name)}
                  options={timezones}
                  value={personalDetails.timeZone} />
              </div>
            </Col>
            <Col lg={12}>
              <div className="hstack gap-2 justify-content-end">
                <Button type="submit" className="btn btn-success">Update</Button>
              </div>
            </Col>
          </Row>
        </Form>
        :
        <Preloader isComponentLoader={true} />
      }

      {personalInfoErr ?
        <ErrorModal isOpen={personalInfoErr} msg={personalInfoErrMsg} resetFunc={resetErrors} />
        : null}
    </React.Fragment>
  )
}

export default PersonalDetails
